<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import {
  Field,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);
// Phase 9.3 (R2-9-5): the role form is a drawer (the kit's EditSheet): the permission list is long.
const open = defineModel('open', { type: Boolean, default: false });
const sheetRef = ref(null);
const snapshot = ref('');

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

const formJSON = () =>
  JSON.stringify([name.value, description.value, selectedPermissions.value]);

watch(open, isOpen => {
  if (!isOpen) return;
  resetForm();
  if (props.mode === 'edit') populateEditForm();
  snapshot.value = formJSON();
});

const dirty = computed(() => open.value && formJSON() !== snapshot.value);
const scope = computed(
  () => `${t('SIDEBAR.SETTINGS')} · ${t('CUSTOM_ROLE.HEADER')}`
);

const togglePermission = (permission, checked) => {
  selectedPermissions.value = checked
    ? [...selectedPermissions.value, permission]
    : selectedPermissions.value.filter(item => item !== permission);
};

const runGuarded = action =>
  sheetRef.value ? sheetRef.value.runGuarded(action) : action();
defineExpose({ runGuarded });

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    open.value = false;
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};
</script>

<template>
  <EditSheet
    ref="sheetRef"
    v-model:open="open"
    :title="modalTitle"
    :scope="scope"
    :dirty="dirty"
    :save-label="submitButtonText"
    :can-save="!addCustomRole.showLoading"
    @save="handleCustomRole"
  >
    <p class="type-polaris-body text-polaris-muted-foreground">
      {{ modalDescription }}
    </p>
    <FoldingGroup :title="$t('SETTINGS_PIECES.SECTIONS.DETAILS')">
      <div class="grid gap-4">
        <Field :data-invalid="v$.name.$error || undefined">
          <FieldLabel for="custom-role-name">
            {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          </FieldLabel>
          <Input
            id="custom-role-name"
            v-model.trim="name"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            :aria-invalid="v$.name.$error || undefined"
            @blur="v$.name.$touch"
          />
          <FieldError v-if="v$.name.$error">
            {{ $t('CUSTOM_ROLE.FORM.NAME.ERROR') }}
          </FieldError>
        </Field>
        <Field :data-invalid="v$.description.$error || undefined">
          <FieldLabel for="custom-role-description">
            {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}
          </FieldLabel>
          <Textarea
            id="custom-role-description"
            v-model="description"
            rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            :aria-invalid="v$.description.$error || undefined"
            @blur="v$.description.$touch"
          />
          <FieldError v-if="v$.description.$error">
            {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.ERROR') }}
          </FieldError>
        </Field>
      </div>
    </FoldingGroup>
    <FoldingGroup
      :title="$t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL')"
      :count="selectedPermissions.length"
    >
      <div class="grid gap-3">
        <Field
          v-for="permission in AVAILABLE_CUSTOM_ROLE_PERMISSIONS"
          :key="permission"
          orientation="horizontal"
        >
          <Checkbox
            :id="`custom-role-${permission}`"
            :model-value="selectedPermissions.includes(permission)"
            @update:model-value="
              checked => togglePermission(permission, checked)
            "
          />
          <FieldLabel :for="`custom-role-${permission}`" class="font-normal">
            {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
          </FieldLabel>
        </Field>
        <FieldError v-if="v$.selectedPermissions.$error">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.ERROR') }}
        </FieldError>
      </div>
    </FoldingGroup>
  </EditSheet>
</template>
